import type { CampaignQuestion, CampaignQuestionOption } from '@/types/campaign-question'

export function parseResponseCount(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, Math.floor(value))
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return Math.max(0, Math.floor(parsed))
    }
  }

  return undefined
}

/** Valor numérico seguro para cálculos (barras e percentuais). */
export function responseCountForMath(value: number | undefined): number {
  return value ?? 0
}

export function formatOptionResponseLabel(count: number | undefined): string {
  if (count === undefined) {
    return 'Sem respostas'
  }

  if (count === 0) {
    return '0 respostas'
  }

  return count === 1 ? '1 resposta' : `${count} respostas`
}

export function formatQuestionTotalResponsesLabel(total: number | undefined): string {
  if (total === undefined) {
    return 'Sem respostas'
  }

  if (total === 0) {
    return '0 respostas no total'
  }

  return total === 1 ? '1 resposta no total' : `${total} respostas no total`
}

function normalizeOption(option: CampaignQuestionOption): CampaignQuestionOption {
  return {
    ...option,
    response_count: parseResponseCount(option.response_count),
  }
}

export function normalizeCampaignQuestion(question: CampaignQuestion): CampaignQuestion {
  const options = (question.options ?? []).map(normalizeOption)
  const totalFromApi = parseResponseCount(question.total_responses)

  if (totalFromApi !== undefined) {
    return { ...question, options, total_responses: totalFromApi }
  }

  const hasAnyCount = options.some((option) => option.response_count !== undefined)
  if (!hasAnyCount) {
    return { ...question, options, total_responses: undefined }
  }

  const totalFromOptions = options.reduce(
    (sum, option) => sum + responseCountForMath(option.response_count),
    0,
  )

  return { ...question, options, total_responses: totalFromOptions }
}

export function normalizeCampaignQuestions(questions: CampaignQuestion[]): CampaignQuestion[] {
  return questions.map(normalizeCampaignQuestion)
}
